import { clamp } from '@utils/math/maths.js';


// ─── Web Audio Context ────────────────────────────────────────────────────────

const INTERACTION_EVENTS = [ 'click', 'touchstart', 'touchend', 'keydown' ];
const AudioCtx = window.AudioContext || window['webkitAudioContext'];
const SAMPLE_RATE = 44100;
const perf = window.performance || window.Date;

let fakeAudioFile = null;
let fakeAudio = null;
let _context = null;
let _master = null;
let _masterVolume = 1;

const _audioState = {
    initialized: false,
    unlocked: false,
    unlockerListening: true,
    lastStuckCheck: 0,
    stuckTimer: 0,
    previousWebAudioTime: 0
};

const _audioEvents = { onContextCreation: null, onUnlock: null, onStuck: null };

function _createFakeAudioFile(sr) {
    const ab = new ArrayBuffer(10);
    const dv = new DataView(ab);
    dv.setUint32(0, sr, true);
    dv.setUint32(4, sr, true);
    dv.setUint16(8, 1, true);
    const missing = window.btoa(String.fromCharCode(...new Uint8Array(ab))).slice(0, 13);
    return `data:audio/wav;base64,UklGRisAAABXQVZFZm10IBAAAAABAAEA${missing}AgAZGF0YQcAAACAgICAgICAAAA=`;
}

function _createContext() {
    try {
        let ctx = new AudioCtx();
        ctx.close();
        ctx = new AudioCtx({ sampleRate: SAMPLE_RATE });
        ctx.suspend();
        return ctx;
    } catch(e) { return null; }
}

function _unlockFakeAudio() {
    return new Promise(resolve => {
        if (!fakeAudio) {
            fakeAudio = document.createElement('audio');
            fakeAudio.setAttribute('x-webkit-airplay', 'deny');
            fakeAudio.preload = 'auto';
            fakeAudio.loop = false;
            fakeAudio.src = fakeAudioFile;
            fakeAudio.load();
        }
        fakeAudio.pause();
        fakeAudio.currentTime = 0;
        try {
            Promise.resolve()
                .then(() => fakeAudio.play())
                .then(() => resolve(true))
                .catch(() => resolve(false));
        } catch(e) { resolve(false); }
    });
}

function _listenUnlocker() {
    _audioState.unlocked = false;
    _audioState.unlockerListening = true;
    for (const ev of INTERACTION_EVENTS)
        document.addEventListener(ev, _unlockAudio, { passive: true, capture: true });
}

function _unlistenUnlocker() {
    _audioState.unlockerListening = false;
    for (const ev of INTERACTION_EVENTS)
        document.removeEventListener(ev, _unlockAudio, { passive: true, capture: true });
}

function _initContext() {
    if (_context) return;
    _context = _createContext();
    const scratchBuffer = _context.scratchBuffer = _context.createBuffer(1, 1, 22050);
    _context.clearBufferSource = (sample) => {
        if (!sample || !sample.source) return;
        sample.source.onended = null;
        sample.source.disconnect(0);
        try { sample.source.buffer = scratchBuffer; sample.buffer = scratchBuffer; } catch(e) { /* */ }
    };
    _master = _context.createGain();
    _master.connect(_context.destination);
    _master.gain.setValueAtTime(_masterVolume, _context.currentTime);
    _audioState.initialized = true;
    if (_audioEvents.onContextCreation) _audioEvents.onContextCreation(_context);
    _checkStuckAudio();
}

function _unlockAudio() {
    if (_audioState.unlocked) return;
    _initContext();
    _context.suspend();
    window.setTimeout(() => _context.resume(), 10);
    _unlockFakeAudio();
    _unlistenUnlocker();
    _audioState.unlocked = true;
    _audioState.stuckTimer = -800;
    _audioState.lastStuckCheck = perf.now();
    if (_audioEvents.onUnlock) _audioEvents.onUnlock();
}

function _checkStuckAudio() {
    window.requestAnimationFrame(_checkStuckAudio);
    const now = perf.now();
    const dt = now - _audioState.lastStuckCheck;
    if (!_audioState.initialized || !_context || dt < 400) return;
    const t = _context.currentTime;
    const isStuck = t === _audioState.previousWebAudioTime;
    _audioState.stuckTimer = isStuck ? _audioState.stuckTimer + dt : 0;
    _audioState.previousWebAudioTime = t;
    _audioState.lastStuckCheck = now;
    if (_audioState.stuckTimer > 400 && _audioState.unlocked && !_audioState.unlockerListening) {
        _listenUnlocker();
        if (_audioEvents.onStuck) _audioEvents.onStuck();
    }
}

function _audioInit() {
    if (_audioState.initialized) return;
    fakeAudioFile = _createFakeAudioFile(SAMPLE_RATE);
    _initContext();
    _listenUnlocker();
    _unlockFakeAudio().then(ok => { if (ok) _unlockAudio(); });
}


// ─── Request / Decode cache ───────────────────────────────────────────────────

const requestPool = {};
const loadPool = {};
const decodedPool = {};

function requestBuffer(path) {
    if (!requestPool[path]) requestPool[path] = new Promise(resolve => {
        const req = new XMLHttpRequest();
        req.open('GET', path, true);
        req.responseType = 'arraybuffer';
        req.onload = () => resolve(req.response);
        req.send();
    });
    return requestPool[path];
}

function loadBuffer(path) {
    if (!_context) return requestBuffer(path);
    if (decodedPool[path]) return Promise.resolve(path);
    if (!loadPool[path]) {
        loadPool[path] = new Promise(resolve => {
            requestBuffer(path).then(ab => {
                _context.decodeAudioData(ab, buf => {
                    decodedPool[path] = buf;
                    resolve(path);
                    loadPool[path] = null;
                });
            });
        });
    }
    return loadPool[path];
}


// ─── AudioSample ─────────────────────────────────────────────────────────────

function deopPlay(src, when, offset, duration) {
    try { src.start(when, offset, duration); return false; } catch(e) { return true; }
}

function deopStop(src) {
    try { src.stop(); return false; } catch(e) { return true; }
}

let sampleUid = 0;

class AudioSample {
    constructor() {
        this.onEnded = this.onEnded.bind(this);
        this.onLoaded = this.onLoaded.bind(this);
        this.context = null;
        this.input = null;
        this.output = null;
    }


    _initOutput() {
        if (!this.output) {
            this.output = _context.createGain();
        }
        this.context = _context;
    }


    reset(opts = {}) {
        this._initOutput();
        this.onStop = null;
        this.stop();

        this.pausedProgress = 0;
        this.fadein = opts.fadein || 0;
        this.fadeout = opts.fadeout || 0;
        this.fadeinTimer = 0;
        this.fadeoutTimer = 0;
        this.fadeinActive = false;
        this.fadeoutActive = false;
        this.needsPlay = false;
        this.isPlaying = false;
        this.isStopping = false;
        this.hasEnded = false;
        this.loaded = false;
        this.stopped = false;
        this.fixedTimeStep = 0;
        this.id = opts.id || null;

        this.clearBuffer();
        this.source = null;
        this.buffer = null;

        this.loop = !!opts.loop;
        this.autoplay = !!opts.autoplay;
        this.playbackRate = opts.playbackRate || 1;
        this.volume = opts.volume != null ? opts.volume : 1;
        this.realvolume = -1;
        this.loopStart = opts.loopStart || 0;
        this.loopEnd = opts.loopEnd || 0;
        this.onStop = opts.onStop || null;
        this.baseOffset = opts.start || opts.baseOffset || 0;
        this.duration = opts.duration;

        const newInput = opts.input || _master;
        if (newInput !== this.input) {
            this.input && this.output.disconnect(this.input);
            this.input = newInput;
            this.output.connect(this.input);
        }

        this.path = opts.path;
        const requestID = this.requestID = ++sampleUid;

        if (decodedPool[this.path]) {
            this.onLoaded(this.path);
        } else {
            loadBuffer(opts.path).then(path => {
                if (this.requestID === requestID) this.onLoaded(path);
            });
        }

        if (this.autoplay) this.play();
    }


    onLoaded(path) {
        if (this.isStopping || this.stopped) return this.realstop();
        const buffer = decodedPool[path];
        this.loaded = true;
        this.buffer = buffer;
        if (this.needsPlay) this.play();
    }


    onEnded() {
        if (this.loop) return;
        this.hasEnded = true;
        this.stop();
    }


    update(dt) {
        if (!this.source) return;
        if (this.fixedTimeStep) dt = this.fixedTimeStep;
        let newVolume = this.volume;

        if (this.fadeinActive) {
            this.fadeinTimer += dt;
            const p = clamp(this.fadeinTimer / this.fadein, 0, 1);
            newVolume *= p;
            if (p === 1) this.fadeinActive = false;
        }
        if (this.fadeoutActive) {
            this.fadeoutTimer += dt;
            const p = clamp(1 - this.fadeoutTimer / this.fadeout, 0, 1);
            newVolume *= p;
            if (p === 0) this.realstop();
        }
        if (newVolume !== this.realvolume) this.setVolume(newVolume);
    }


    play() {
        if (this.source) this.stop();
        if (!this.loaded) { this.needsPlay = true; return; }

        const source = this.source = _context.createBufferSource();
        source.buffer = this.buffer;
        source.onended = this.onEnded;
        this.startTime = _context.currentTime;
        this.isPlaying = true;
        this.needsPlay = false;

        if (!source || !source.buffer) return this.stop();

        this.progressStart = this.startTime - (this.offset || 0);
        if (!this.duration) this.duration = source.buffer.duration;
        const duration = Math.max(this.duration - (this.offset || 0), 0.01);

        this.connect();
        this.setLoop(this.loop);

        if (!this.isStopping) {
            const error = deopPlay(
                this.source, this.startTime,
                (this.baseOffset + (this.offset || 0)) % source.buffer.duration,
                this.loop ? undefined : duration
            );
            if (error) { requestAnimationFrame(() => this.stop()); return; }
        }

        if (this.fadein && this.fadeinTimer === 0) this.setVolume(0, true);
        else this.setVolume(this.volume, true);

        if (this.fadein) this.fadeinActive = true;
        this.setPlaybackRate(this.playbackRate);
    }


    connect() { this.source.connect(this.output); }
    disconnect() { this.source.disconnect(this.output); }


    clearBuffer() {
        if (this.source) _context && _context.clearBufferSource && _context.clearBufferSource(this);
        this.source = null;
    }


    pause() {
        this.pausedProgress = this.getProgress();
        this.offset = (this.offset || 0) + (_context.currentTime - this.startTime) * this.playbackRate;
        this.needsPlay = false;
        this.isPlaying = false;
        if (!this.source) return;
        deopStop(this.source);
        this.clearBuffer();
    }


    realstop() {
        if (this.onStop) this.onStop(this);
        this.onStop = null;
        this.offset = 0;
        this.needsPlay = false;
        this.isPlaying = false;
        this.isStopping = false;
        this.fadeinActive = false;
        this.fadeoutActive = false;
        this.stopped = true;
        if (!this.source) return;
        deopStop(this.source);
        this.clearBuffer();
    }


    stop(opts = {}) {
        if (this.isStopping) return;
        this.isStopping = true;
        if (!this.source || !this.loaded) return this.realstop();
        if (opts.fadeout) this.fadeout = opts.fadeout;
        if (this.fadeout) this.fadeoutActive = true;
        else this.realstop();
    }


    getProgress() {
        return this.isPlaying
            ? (_context.currentTime - this.progressStart) / this.duration
            : this.pausedProgress;
    }


    setPlaybackRate(value) {
        this.playbackRate = value;
        if (!this.isPlaying || !this.source) return;
        this.source.playbackRate.setTargetAtTime(value, _context.currentTime, 0.01);
    }


    setLoop(value) {
        this.loop = value;
        if (!this.isPlaying || !this.source) return;
        this.source.loop = this.loop;
        this.source.loopStart = this.loopStart;
        this.source.loopEnd = this.loopEnd;
    }


    setVolume(value, first, timeConstant = 0.01) {
        this.realvolume = value;
        if (first) this.output.gain.value = value;
        this.output.gain.setTargetAtTime(value, _context.currentTime, timeConstant);
    }


    release() { AudioSample.release(this); }
}


AudioSample.pool = [];
AudioSample.get = function(opts) {
    const instance = AudioSample.pool.pop() || new AudioSample();
    instance.reset(opts);
    return instance;
};
AudioSample.release = function(instance) {
    instance.clearBuffer();
    instance.buffer = null;
    instance.onStop = null;
    if (AudioSample.pool.length < 50) AudioSample.pool.push(instance);
};


// ─── AudioManager ─────────────────────────────────────────────────────────────

export default class AudioManager {
    constructor() {
        this.isUnlocked = false;
        this.isMuted = false;

        this._list = {};
        this._activeSamples = {};
        this._throttleSamples = {};
        this._delayed = new Set();
        this._unlockCallbacks = new Set();
        this._initialized = false;
        this._dt = 16.66;

        const savedMuted = !!+localStorage.getItem('audio-muted');
        if (savedMuted) this.isMuted = true;

        _audioEvents.onContextCreation = () => {};
        _audioEvents.onUnlock = () => this._onUnlock();
        _audioEvents.onStuck = () => { this.stop(); this.isUnlocked = false; };
    }


    init(list = {}) {
        if (this._initialized) return;
        this._initialized = true;
        this._list = list;
        _audioInit();
    }


    onUnlock(cb) {
        this._unlockCallbacks.add(cb);
        if (this.isUnlocked) cb();
        return () => this._unlockCallbacks.delete(cb);
    }


    play(id, opts = {}) {
        return this._playSound(id, opts);
    }


    stop(id) {
        if (id) {
            this._stopSound(id);
        } else {
            for (const k in this._activeSamples) {
                this._activeSamples[k].forEach(i => i.stop());
            }
        }
    }


    preload(id) {
        const sample = this._list[id];
        if (!sample) return;
        return loadBuffer(sample.path);
    }


    update(dt) {
        if (!_audioState.unlocked) return;
        this._dt = dt || 16.66;

        this._delayed.forEach(sound => {
            if ((sound.timer -= this._dt) > 0) return;
            this._delayed.delete(sound);
            this._playSound(sound.id, sound.opts);
        });

        this._updateVolumes();
        for (const k in this._activeSamples) {
            this._activeSamples[k].forEach(i => i.update(this._dt));
        }
    }


    setList(list) { this._list = list; }


    setMuted(v) {
        this.isMuted = v;
        localStorage.setItem('audio-muted', +!!v);
        this._updateVolumes();
    }


    toggleMute() { this.setMuted(!this.isMuted); }


    _onUnlock() {
        this.isUnlocked = true;
        this._unlockCallbacks.forEach(cb => cb());
    }


    _updateVolumes() {
        const v = this.isMuted ? 0 : 1;
        if (!_context || !_master) return;
        _master.gain.setTargetAtTime(v, _context.currentTime, 0.01);
    }


    _playSound(id, opts = {}) {
        if (!_audioState.unlocked) return;
        const sample = this._list[id];
        if (!sample) return;

        if (opts.delay) {
            const timer = opts.delay;
            const delayOpts = { ...opts };
            delete delayOpts.delay;
            this._delayed.add({ id, opts: delayOpts, timer });
            return;
        }

        const now = performance.now();
        if (opts.throttle) {
            if (!this._throttleSamples[id]) this._throttleSamples[id] = 0;
            if (now - this._throttleSamples[id] < opts.throttle) return;
        }
        this._throttleSamples[id] = now;

        const unique = opts.unique || sample.unique;
        if (!this._activeSamples[id]) this._activeSamples[id] = new Set();

        if (unique && this._activeSamples[id].size > 0) {
            const inst = this._activeSamples[id].values().next().value;
            if (!inst.isPlaying || inst.isStopping) inst.realstop();
            else return inst;
        }

        const finalOpts = {
            ...opts,
            id,
            path: sample.path,
            onStop: i => this._onSoundStop(i),
            autoplay: opts.autoplay ?? true,
            loop: opts.loop ?? !!sample.loop,
            fadein: opts.fadein ?? sample.fadein,
            fadeout: opts.fadeout ?? sample.fadeout,
            volume: opts.volume ?? sample.volume ?? 1
        };

        const instance = AudioSample.get(finalOpts);
        this._activeSamples[id].add(instance);
        return instance;
    }


    _stopSound(id, opts = {}) {
        const map = this._activeSamples[id];
        if (!map) return;
        map.forEach(i => i.stop(opts));
    }


    _onSoundStop(instance) {
        instance.release();
        const map = this._activeSamples[instance.id];
        if (map) map.delete(instance);
    }
}
